<template>
  <div class="register">
    <router-link v-if="!isResetPassword"
                 to="/user/sign-in"
                 class="register__back">
      <span class="register__back_arrow"></span> BACK
    </router-link>
    <p class="register__title" :class="{'register__title_sign-in': isSent || isResetPassword}">{{ title }}</p>
    <p v-if="!isResetPassword || isSent" class="register__desc" v-html="desc"></p>
    <template v-if="!isSent">
      <div v-for="input in filteredFields"
           :key="input.id"
           class="register__input_wrapper"
           :class="[`register__input_wrapper_${input.id}`, { 'register__input_wrapper_error': errorFields[input.id] }]">
        <p class="register__input_desc" v-if="input.desc">{{ input.desc }}</p>
        <label class="register__input_label" :for="input.id">{{ input.label }}</label>
        <input :type="input.type"
               :id="input.id"
               v-model="data[input.id]"
               :disabled="isDataUpdating"
               class="register__input"
               :class="{ 'register__input_error': errorFields[input.id] }"
               :placeholder="input.placeholder">
        <base-material-design-icon class="register__input_icon" :icon="input.icon"/>
        <span v-if="errorFields[input.id]" class="register__input_error-message">{{ errorFields[input.id] }}</span>
      </div>
    </template>
    <button :disabled="isDataUpdating"
            class="register__submit"
            :class="{'register__submit_sign-in': isSent || isResetPassword}"
            @click.prevent="onSubmit">
      {{ submit }}
    </button>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'

const FIELDS = [
  {
    id: 'name',
    label: 'First Name',
    placeholder: 'Enter your first name',
    type: 'text',
    icon: 'accountBold'
  },
  {
    id: 'surname',
    label: 'Last Name',
    placeholder: 'Enter your last name',
    type: 'text',
    icon: 'accountBold'
  },
  {
    id: 'email',
    label: 'Email Address',
    placeholder: 'Enter your email',
    type: 'email',
    icon: 'email'
  },
  {
    desc: 'Password must have at least 6 characters with one lowercase letter, one uppercase letter and one number.',
    id: 'password',
    label: 'Password',
    placeholder: 'Enter your password',
    type: 'password',
    icon: 'password'
  },
  {
    id: 'confirm-password',
    label: 'Confirm Password',
    placeholder: 'Confirm your password',
    type: 'password',
    icon: 'password'
  }
]
export default {
  name: 'Register',
  data () {
    return {
      errorFields: {},
      isSent: false,
      isDataUpdating: false,
      data: {
        name: '',
        surname: '',
        email: '',
        password: '',
        'confirm-password': ''
      }
    }
  },
  computed: {
    isResetPassword () {
      return this.userId && this.userId.length
    },
    userId () {
      return this.$route.params.id || ''
    },
    filteredFields () {
      return this.isResetPassword
        ? _.cloneDeep(FIELDS).filter(i => ['confirm-password', 'password'].includes(i.id))
        : _.cloneDeep(FIELDS)
    },
    title () {
      return this.isSent && !this.isResetPassword
        ? 'Confirm your email'
        : !this.isResetPassword ? 'Create New Account' : 'Reset Password'
    },
    submit () {
      return this.isResetPassword && !this.isSent ? 'CHANGE PASSWORD' : this.isSent ? 'SIGN IN' : 'SIGN UP'
    },
    desc () {
      return this.isResetPassword
        ? 'Your password has been changed.'
        : this.isSent
          // ? 'Thanks for registering - please check your email for account confirmation details'
          ? `Thanks for registering - someone from our team will reach out to you at <br><span style="font-weight: bold">‘${this.data.email}’</span> with instructions on how to complete your registration`
          : 'Please, fill in all needed fields to complete registration.'
    }
  },
  methods: {
    ...mapActions('auth', ['registerUser']),
    async onSubmit () {
      if (!this.isSent) {
        this.isDataUpdating = true
        const response = await this.registerUser({
          ...this.data,
          userId: this.userId,
          method: this.isResetPassword ? 'patch' : 'post'
        })
        this.errorFields = response.error_fields || {}
        this.isSent = response.is_successful
        this.isDataUpdating = false
      } else {
        this.$router.push({ path: '/user/sign-in' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .register {
    width: 394px;
    background-color: #fff;
    border-radius: 16px;
    box-shadow: 0 6px 16px 0 rgba(96, 97, 112, 0.2), 0 1px 4px 0 rgba(40, 41, 61, 0.06);
    padding: 48px;

    &__submit {
      display: block;
      margin: 48px auto 0;
      outline: none;
      border: none;
      padding: 16px 59px;
      line-height: 24px;
      font-size: 16px;
      font-weight: bold;
      color: #fff;
      background-color: $rfx-bright-turquoise;
      border-radius: 8px;

      &:disabled {
        pointer-events: none;
        opacity: .4;
      }
    }

    &__input {
      display: inline-block;
      width: -webkit-fill-available;
      line-height: 24px;
      border-radius: 8px;
      border: solid 1px #7c8e9b;
      padding: 16px 16px 16px 56px;
      color: $rfx-medium-march !important;
      margin: 1px;

      &:disabled {
        background-color: #7c8e9b40;
      }

      &::placeholder {
        color: $rfx-medium-march;
      }

      &:focus {
        border: 2px solid $rfx-bright-turquoise;
        outline: none;
        margin: 0;
      }

      &_desc {
        color: $rfx-medium-march;
        font-size: 14px;
        line-height: 20px;
        padding: 0;
        margin: 0 0 16px;
      }

      &_error {
        border-color: $rfx-light-red;
      }

      &_error-message {
        display: inline-block;
        color: $rfx-light-red;
        margin-top: 8px;
        font-size: 14px;
        line-height: 16px;
      }

      &_wrapper {
        margin-bottom: 24px;

        &_error {
          margin-bottom: 16px;
        }

        &_confirm-password {
          margin-bottom: 0 !important;
        }
      }

      &_label {
        font-size: 14px;
        line-height: 16px;
        display: inline-block;
        font-weight: 500;
        color: $rfx-medium-march;
        margin-bottom: 8px;
      }

      &_icon {
        display: block;
        font-size: 24px;
        color: $rfx-medium-march;
        width: 0;
        height: 0;
        position: relative;
        left: 18px;
        top: -18px;
      }
    }

    &__desc {
      margin: 0 0 24px;
      padding: 0;
      font-size: 14px;
      line-height: 20px;
      color: $rfx-medium-march;
    }

    &__title {
      margin: 0 0 16px;
      padding: 0;
      color: $rfx-dark-march;
      font-size: 32px;
      font-weight: bold;
    }

    &__back {
      display: inline-block;
      color: $rfx-dark-march;
      font-size: 14px;
      line-height: 24px;
      font-weight: bold;
      margin-bottom: 24px;
      text-decoration: none;

      &_arrow {
        margin-right: 20px;
        padding: 4px;
        border: solid $rfx-dark-march;
        border-width: 0 2px 2px 0;
        display: inline-block;
        transform: rotate(135deg);
      }
    }
  }

  @media only screen and (max-width: 490px) {
    .register {
      width: -webkit-fill-available;
      min-height: -webkit-fill-available;
      padding: 24px 24px 32px;
      border-radius: 0;
      position: absolute;
      top: 0;
      display: flex;
      flex-direction: column;

      &__back {
        padding-left: 12px;
        margin-bottom: 16px;
        font-weight: bold;
      }

      &__title {
        font-size: 24px;
        font-weight: bold;

        &_sign-in {
          margin-top: auto;
        }
      }

      &__desc {
        margin-bottom: 16px;
      }

      &__input {
        &_wrapper {
          margin-bottom: 16px;
        }
      }

      &__submit {
        margin-top: 24px;

        &_sign-in {
          margin-top: 32px;
          margin-bottom: auto;
        }
      }
    }
  }
</style>
